/**
 * CalloutBlock — Notion-style callout block for MarthaEditor.
 * Features left colored border, icon, and editable inline content.
 */

import { createReactBlockSpec } from '@blocknote/react';

type CalloutVariant = 'info' | 'warning' | 'success' | 'error';

const VARIANT_STYLES: Record<CalloutVariant, { border: string; bg: string; icon: string }> = {
  info: { border: 'border-l-blue-500', bg: 'bg-blue-50', icon: '\u{1F4A1}' },
  warning: { border: 'border-l-amber-500', bg: 'bg-amber-50', icon: '\u26A0\uFE0F' },
  success: { border: 'border-l-emerald-500', bg: 'bg-emerald-50', icon: '\u2705' },
  error: { border: 'border-l-red-500', bg: 'bg-red-50', icon: '\u{1F6A8}' },
};

interface CalloutBlockRendererProps {
  block: {
    id: string;
    props: { variant: string };
  };
  editor: {
    updateBlock: (id: string, update: { props: Partial<{ variant: string }> }) => void;
    isEditable: boolean;
  };
  contentRef: React.RefObject<HTMLDivElement | null>;
}

function CalloutBlockRenderer({ block, editor, contentRef }: CalloutBlockRendererProps) {
  const variant = (block.props.variant || 'info') as CalloutVariant;
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.info;
  const isEditable = editor.isEditable;

  return (
    <div
      className={`callout-block flex items-start gap-3 px-4 py-3 rounded-r-lg border-l-4 ${styles.border} ${styles.bg} my-1`}
      contentEditable={false}
    >
      {/* Icon — clickable to cycle variants when editable */}
      <button
        type="button"
        className="text-lg shrink-0 mt-0.5 cursor-pointer select-none hover:scale-110 transition-transform"
        onClick={() => {
          if (!isEditable) return;
          const variants: CalloutVariant[] = ['info', 'warning', 'success', 'error'];
          const nextIdx = (variants.indexOf(variant) + 1) % variants.length;
          editor.updateBlock(block.id, { props: { variant: variants[nextIdx] } });
        }}
        title={isEditable ? `Click to change style (${variant})` : variant}
      >
        {styles.icon}
      </button>

      {/* Editable content area */}
      <div
        ref={contentRef}
        className="flex-1 min-w-0 text-sm text-stone-700 outline-none"
        contentEditable={isEditable}
        suppressContentEditableWarning
      />
    </div>
  );
}

/**
 * Callout block spec for BlockNote.
 */
export const calloutBlockSpec = createReactBlockSpec(
  {
    type: 'callout' as const,
    propSchema: {
      variant: { default: 'info' },
    },
    content: 'inline',
  },
  {
    render: (props) => {
      const blockProps: CalloutBlockRendererProps = {
        block: {
          id: props.block.id,
          props: props.block.props as { variant: string },
        },
        editor: {
          updateBlock: (id, update) => (props.editor as any).updateBlock(id, update),
          isEditable: (props.editor as any).isEditable,
        },
        contentRef: props.contentRef as React.RefObject<HTMLDivElement | null>,
      };
      return <CalloutBlockRenderer {...blockProps} />;
    },
  }
);

export default calloutBlockSpec;
